import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  ClipboardList,
  Cog,
  FileDown,
  FileUp,
  Pencil,
  Plus,
  Tags,
} from "lucide-react";

import { ApiError, formatCLP } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import { ImportarPlanillaModal } from "../../components/ImportarPlanillaModal";
import {
  Badge,
  type BadgeTone,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  actualizarActivo,
  actualizarTipoActivo,
  cambiarEstadoActivo,
  crearActivo,
  crearTipoActivo,
  descargarPlantillaActivos,
  exportarActivos,
  hojaVidaActivo,
  importarActivos,
  listarActivos,
  listarTiposActivo,
  numeroOT,
  ESTADO_OT_LABELS,
  TIPO_OT_LABELS,
  type ActivoCreate,
  type ActivoRead,
  type ActivoUpdate,
  type Conservacion,
  type EstadoActivo,
  type EstadoOT,
  type HojaVidaActivo,
  type TipoActivoCreate,
  type TipoActivoRead,
  type TipoActivoUpdate,
} from "./api";

const ESTADOS: readonly EstadoActivo[] = [
  "Operativo",
  "EnMantencion",
  "Falla",
  "Baja",
];

const ESTADO_LABEL: Record<EstadoActivo, string> = {
  Operativo: "Operativo",
  EnMantencion: "En mantención",
  Falla: "Falla",
  Baja: "Baja",
};

const ESTADO_TONE: Record<EstadoActivo, BadgeTone> = {
  Operativo: "emerald",
  EnMantencion: "amber",
  Falla: "rose",
  Baja: "slate",
};

const CONSERVACIONES: readonly Conservacion[] = ["Bueno", "Regular", "Malo"];

const CONSERVACION_TONE: Record<Conservacion, BadgeTone> = {
  Bueno: "emerald",
  Regular: "amber",
  Malo: "rose",
};

const OT_TONE: Record<EstadoOT, BadgeTone> = {
  Pendiente: "slate",
  Asignada: "brand",
  EnProceso: "amber",
  EsperaRepuestos: "amber",
  Finalizada: "emerald",
  Cancelada: "rose",
};

export function ActivosListPage(): JSX.Element {
  const [editing, setEditing] = useState<ActivoRead | "new" | null>(null);
  const [tiposOpen, setTiposOpen] = useState(false);
  const [estadoDe, setEstadoDe] = useState<ActivoRead | null>(null);
  const [hojaDe, setHojaDe] = useState<ActivoRead | null>(null);
  const [importando, setImportando] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const activosQ = useQuery({
    queryKey: ["mantenimiento", "activos"],
    queryFn: listarActivos,
  });
  const tiposQ = useQuery({
    queryKey: ["mantenimiento", "tipos-activo"],
    queryFn: listarTiposActivo,
  });

  const tipos = tiposQ.data ?? [];
  const nombrePorTipo = new Map<number, string>(
    tipos.map((t) => [t.id, t.nombre]),
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Activos"
        icon={Cog}
        subtitle={activosQ.data && (
          <span>({activosQ.data.length})</span>
        )}
        actions={
          <>
            <Button variant="secondary" icon={Tags} onClick={() => setTiposOpen(true)}>
              Tipos de activo
            </Button>
            <Button variant="secondary" icon={FileUp} onClick={() => setImportando(true)}>
              Importar Excel
            </Button>
            <Button
              variant="secondary"
              icon={FileDown}
              onClick={() => {
                exportarActivos().catch((e: unknown) =>
                  toast.error(e instanceof ApiError ? e.message : "No se pudo exportar"),
                );
              }}
              disabled={!activosQ.data || activosQ.data.length === 0}
            >
              Exportar Excel
            </Button>
            <Button icon={Plus} onClick={() => setEditing("new")}>
              Nuevo activo
            </Button>
          </>
        }
      />

      <p className="text-sm text-neutral-500">
        Infraestructura física del comité: captaciones, estanques, bombas,
        redes. No confundir con los medidores comerciales.
      </p>

      {editing && (
        <ActivoForm
          activo={editing === "new" ? null : editing}
          tipos={tipos}
          onClose={() => setEditing(null)}
        />
      )}

      {importando && (
        <ImportarPlanillaModal
          titulo="Importar activos"
          descripcion={
            <>
              Carga el levantamiento de activos desde Excel. Obligatorias: <b>nombre</b> y{" "}
              <b>tipo</b>. Sin código, el sistema asigna <b>ACT-0001</b>, <b>ACT-0002</b>… (es el
              que va en la etiqueta). Con código, actualiza el activo que ya lo tenga.
            </>
          }
          importar={importarActivos}
          descargarPlantilla={descargarPlantillaActivos}
          detalle={(r) =>
            r.tipos_creados.length > 0
              ? [`Tipos de activo nuevos: ${r.tipos_creados.join(", ")}`]
              : []
          }
          onClose={() => setImportando(false)}
          onImportado={() => {
            void qc.invalidateQueries({ queryKey: ["mantenimiento"] });
          }}
        />
      )}

      {tiposOpen && (
        <TiposModal tipos={tipos} onClose={() => setTiposOpen(false)} />
      )}

      {estadoDe && (
        <EstadoModal activo={estadoDe} onClose={() => setEstadoDe(null)} />
      )}

      {hojaDe && (
        <HojaVidaModal activo={hojaDe} onClose={() => setHojaDe(null)} />
      )}

      {activosQ.isPending && <Cargando />}
      {activosQ.isError && (
        <ErrorCarga
          error={activosQ.error}
          mensaje="Error al cargar activos."
          onReintentar={() => activosQ.refetch()}
        />
      )}

      {activosQ.data && activosQ.data.length === 0 && (
        <EmptyState
          icon={Cog}
          title="Sin activos todavía"
          description="Carga la infraestructura del comité para poder generar órdenes de trabajo sobre ella."
        >
          <Button icon={Plus} onClick={() => setEditing("new")}>
            Nuevo activo
          </Button>
        </EmptyState>
      )}

      {activosQ.data && activosQ.data.length > 0 && (
        <TableWrap label="Activos">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Nombre</th>
                <th scope="col" className="px-3 py-2 text-left">Código</th>
                <th scope="col" className="px-3 py-2 text-left">Tipo</th>
                <th scope="col" className="px-3 py-2 text-left">Ubicación</th>
                <th scope="col" className="px-3 py-2 text-left">Conservación</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {activosQ.data.map((a) => (
                <ActivoRow
                  key={a.id}
                  activo={a}
                  tipoNombre={nombrePorTipo.get(a.tipo_id) ?? "—"}
                  onEdit={() => setEditing(a)}
                  onEstado={() => setEstadoDe(a)}
                  onHoja={() => setHojaDe(a)}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function ActivoRow({
  activo,
  tipoNombre,
  onEdit,
  onEstado,
  onHoja,
}: {
  activo: ActivoRead;
  tipoNombre: string;
  onEdit: () => void;
  onEstado: () => void;
  onHoja: () => void;
}): JSX.Element {
  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-3 py-2 font-medium text-neutral-900">
        {activo.nombre}
        {(activo.marca || activo.modelo) && (
          <div className="text-xs text-neutral-500 font-normal">
            {[activo.marca, activo.modelo].filter(Boolean).join(" · ")}
          </div>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-neutral-700">
        {activo.codigo_interno ?? "—"}
      </td>
      <td className="px-3 py-2 text-neutral-600">{tipoNombre}</td>
      <td className="px-3 py-2 text-neutral-600">
        {activo.ubicacion ?? "—"}
        {activo.responsable && (
          <div className="text-xs text-neutral-500">A cargo: {activo.responsable}</div>
        )}
      </td>
      <td className="px-3 py-2">
        {activo.estado_conservacion ? (
          <Badge tone={CONSERVACION_TONE[activo.estado_conservacion]}>
            {activo.estado_conservacion}
          </Badge>
        ) : (
          <span className="text-neutral-500">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <Badge tone={ESTADO_TONE[activo.estado]}>
          {ESTADO_LABEL[activo.estado]}
        </Badge>
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-3">
          <Button variant="link" size="sm" icon={Pencil} onClick={onEdit}>
            Editar
          </Button>
          <Button variant="link" size="sm" icon={Activity} onClick={onEstado}>
            Estado
          </Button>
          <Button variant="link" size="sm" icon={ClipboardList} onClick={onHoja}>
            Hoja de vida
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Modal de cambio de estado
// ────────────────────────────────────────────────────────────────────────

function EstadoModal({
  activo,
  onClose,
}: {
  activo: ActivoRead;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [estado, setEstado] = useState<EstadoActivo>(activo.estado);

  const mut = useMutation({
    mutationFn: (e: EstadoActivo) => cambiarEstadoActivo(activo.id, e),
    onSuccess: (a) => {
      toast.success(`Estado de "${a.nombre}" actualizado`);
      void qc.invalidateQueries({ queryKey: ["mantenimiento", "activos"] });
      onClose();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "No se pudo actualizar");
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Cambiar estado"
      subtitle={activo.nombre}
      icon={Activity}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate(estado)} disabled={mut.isPending}>
            {mut.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Select
          label="Estado destino"
          value={estado}
          onChange={(e) => setEstado(e.target.value as EstadoActivo)}
          disabled={mut.isPending}
        >
          {ESTADOS.map((e) => (
            <option key={e} value={e}>
              {ESTADO_LABEL[e]}
            </option>
          ))}
        </Select>
        <p className="text-sm text-neutral-500">
          Baja es un estado terminal (no se puede reactivar).
        </p>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Modal de hoja de vida
// ────────────────────────────────────────────────────────────────────────

function HojaVidaModal({
  activo,
  onClose,
}: {
  activo: ActivoRead;
  onClose: () => void;
}): JSX.Element {
  const hojaQ = useQuery<HojaVidaActivo>({
    queryKey: ["mantenimiento", "activos", activo.id, "hoja-vida"],
    queryFn: () => hojaVidaActivo(activo.id),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Hoja de vida"
      subtitle={activo.nombre}
      icon={ClipboardList}
      size="lg"
    >
      {hojaQ.isPending && <Cargando />}
      {hojaQ.isError && (
        <ErrorCarga
          error={hojaQ.error}
          mensaje="Error al cargar la hoja de vida."
          onReintentar={() => hojaQ.refetch()}
        />
      )}

      {hojaQ.data && (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
            <Dato label="Código" value={hojaQ.data.activo.codigo_interno ?? "—"} />
            <Dato label="Marca" value={hojaQ.data.activo.marca ?? "—"} />
            <Dato label="Modelo" value={hojaQ.data.activo.modelo ?? "—"} />
            <Dato label="N° serie" value={hojaQ.data.activo.numero_serie ?? "—"} />
            <Dato label="Ubicación" value={hojaQ.data.activo.ubicacion ?? "—"} />
            <Dato
              label="Instalación"
              value={hojaQ.data.activo.fecha_instalacion ?? "—"}
            />
            <div>
              <dt className="text-xs uppercase tracking-wider text-neutral-500">
                Estado
              </dt>
              <dd className="mt-0.5">
                <Badge tone={ESTADO_TONE[hojaQ.data.activo.estado]}>
                  {ESTADO_LABEL[hojaQ.data.activo.estado]}
                </Badge>
              </dd>
            </div>
          </dl>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-neutral-900">
              Órdenes de trabajo
            </h3>
            {hojaQ.data.ordenes.length === 0 ? (
              <p className="text-sm text-neutral-500">
                Sin intervenciones registradas.
              </p>
            ) : (
              <TableWrap label="Hoja de vida">
                <table className="min-w-full divide-y divide-neutral-200 text-sm">
                  <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left">N°</th>
                      <th scope="col" className="px-3 py-2 text-left">Tipo</th>
                      <th scope="col" className="px-3 py-2 text-left">Estado</th>
                      <th scope="col" className="px-3 py-2 text-left">Descripción</th>
                      <th scope="col" className="px-3 py-2 text-right">Costo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {hojaQ.data.ordenes.map((o) => (
                      <tr key={o.id} className="hover:bg-neutral-50">
                        <td className="px-3 py-2 whitespace-nowrap tabular-nums text-neutral-700">
                          {numeroOT(o)}
                        </td>
                        <td className="px-3 py-2 text-neutral-600">{TIPO_OT_LABELS[o.tipo]}</td>
                        <td className="px-3 py-2">
                          <Badge tone={OT_TONE[o.estado]}>{ESTADO_OT_LABELS[o.estado]}</Badge>
                        </td>
                        <td className="max-w-xs truncate px-3 py-2 text-neutral-600">
                          {o.descripcion_solicitud}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-neutral-700">
                          {formatCLP(o.costo_total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Dato({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd className="mt-0.5 text-neutral-800">{value}</dd>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Form alta/edición de activo
// ────────────────────────────────────────────────────────────────────────

function ActivoForm({
  activo,
  tipos,
  onClose,
}: {
  activo: ActivoRead | null;
  tipos: TipoActivoRead[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const editando = activo !== null;

  const [tipoId, setTipoId] = useState(
    activo != null ? String(activo.tipo_id) : "",
  );
  const [nombre, setNombre] = useState(activo?.nombre ?? "");
  const [codigo, setCodigo] = useState(activo?.codigo_interno ?? "");
  const [marca, setMarca] = useState(activo?.marca ?? "");
  const [modelo, setModelo] = useState(activo?.modelo ?? "");
  const [numeroSerie, setNumeroSerie] = useState(activo?.numero_serie ?? "");
  const [ubicacion, setUbicacion] = useState(activo?.ubicacion ?? "");
  const [fechaInstalacion, setFechaInstalacion] = useState(
    activo?.fecha_instalacion ?? "",
  );
  const [conservacion, setConservacion] = useState<Conservacion | "">(
    activo?.estado_conservacion ?? "",
  );
  const [responsable, setResponsable] = useState(activo?.responsable ?? "");
  const [fechaAdquisicion, setFechaAdquisicion] = useState(activo?.fecha_adquisicion ?? "");
  const [valorAdquisicion, setValorAdquisicion] = useState(
    activo?.valor_adquisicion != null ? String(activo.valor_adquisicion) : "",
  );
  const [documentoCompra, setDocumentoCompra] = useState(activo?.documento_compra ?? "");
  const [observacion, setObservacion] = useState(activo?.observacion ?? "");
  const [error, setError] = useState<string | null>(null);
  const etiquetado = activo?.etiquetado_at != null;

  const crearMut = useMutation({
    mutationFn: (p: ActivoCreate) => crearActivo(p),
    onSuccess: (a) => {
      toast.success(`Activo "${a.nombre}" creado`);
      void qc.invalidateQueries({ queryKey: ["mantenimiento", "activos"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  const actualizarMut = useMutation({
    mutationFn: (p: ActivoUpdate) =>
      actualizarActivo((activo as ActivoRead).id, p),
    onSuccess: (a) => {
      toast.success(`Activo "${a.nombre}" actualizado`);
      void qc.invalidateQueries({ queryKey: ["mantenimiento", "activos"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const isPending = crearMut.isPending || actualizarMut.isPending;
  const sinTipos = tipos.length === 0;

  function submit(): void {
    setError(null);
    if (tipoId === "") return setError("Tipo de activo requerido");
    if (!nombre.trim()) return setError("Nombre requerido");
    const valor = valorAdquisicion.replace(/[.$\s]/g, "");
    if (valor !== "" && !/^\d+$/.test(valor)) {
      return setError("Valor de adquisición: sólo pesos, sin decimales");
    }

    const payload = {
      tipo_id: Number(tipoId),
      nombre: nombre.trim(),
      codigo_interno: codigo.trim() || null,
      marca: marca.trim() || null,
      modelo: modelo.trim() || null,
      numero_serie: numeroSerie.trim() || null,
      ubicacion: ubicacion.trim() || null,
      fecha_instalacion: fechaInstalacion || null,
      estado_conservacion: conservacion || null,
      responsable: responsable.trim() || null,
      fecha_adquisicion: fechaAdquisicion || null,
      valor_adquisicion: valor === "" ? null : Number(valor),
      documento_compra: documentoCompra.trim() || null,
      observacion: observacion.trim() || null,
    };
    if (editando) actualizarMut.mutate(payload);
    else crearMut.mutate(payload);
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([
    tipoId, nombre, codigo, marca, modelo, numeroSerie, ubicacion, fechaInstalacion,
    conservacion, responsable, fechaAdquisicion, valorAdquisicion, documentoCompra,
    observacion,
  ]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    crearMut.isPending || actualizarMut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={editando ? "Editar activo" : "Nuevo activo"}
      {...(editando ? { subtitle: activo.nombre } : {})}
      icon={Cog}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isPending || (!editando && sinTipos)}>
            {isPending
              ? editando
                ? "Guardando…"
                : "Creando…"
              : editando
                ? "Guardar cambios"
                : "Crear activo"}
          </Button>
        </>
      }
    >
      {!editando && sinTipos && (
        <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Crea primero un tipo de activo.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Select
          label="Tipo de activo"
          value={tipoId}
          onChange={(e) => setTipoId(e.target.value)}
          disabled={isPending}
        >
          <option value="">— Selecciona un tipo —</option>
          {tipos.map((t) => (
            <option key={t.id} value={String(t.id)}>
              {t.nombre}
            </option>
          ))}
        </Select>
        <Input
          label="Nombre"
          required
          type="text"
          maxLength={120}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          disabled={isPending}
          autoFocus
        />
        <Input
          label="Código interno"
          type="text"
          maxLength={40}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          disabled={isPending || etiquetado}
          placeholder={editando ? "" : "Vacío = se asigna ACT-0001…"}
          hint={etiquetado ? "Ya tiene etiqueta impresa: el código no cambia." : undefined}
        />
        <Input
          label="Ubicación"
          type="text"
          maxLength={120}
          value={ubicacion}
          onChange={(e) => setUbicacion(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Marca"
          type="text"
          maxLength={80}
          value={marca}
          onChange={(e) => setMarca(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Modelo"
          type="text"
          maxLength={80}
          value={modelo}
          onChange={(e) => setModelo(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="N° de serie"
          type="text"
          maxLength={80}
          value={numeroSerie}
          onChange={(e) => setNumeroSerie(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Fecha de instalación"
          type="date"
          value={fechaInstalacion}
          onChange={(e) => setFechaInstalacion(e.target.value)}
          disabled={isPending}
        />
      </div>

      <h3 className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
        Levantamiento
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Select
          label="Estado de conservación"
          value={conservacion}
          onChange={(e) => setConservacion(e.target.value as Conservacion | "")}
          disabled={isPending}
        >
          <option value="">— Sin registrar —</option>
          {CONSERVACIONES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <Input
          label="Responsable"
          type="text"
          maxLength={150}
          value={responsable}
          onChange={(e) => setResponsable(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Fecha de adquisición"
          type="date"
          value={fechaAdquisicion}
          onChange={(e) => setFechaAdquisicion(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Valor de adquisición ($)"
          type="text"
          inputMode="numeric"
          value={valorAdquisicion}
          onChange={(e) => setValorAdquisicion(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Documento de compra"
          type="text"
          maxLength={50}
          value={documentoCompra}
          onChange={(e) => setDocumentoCompra(e.target.value)}
          disabled={isPending}
          placeholder="ej. FAC 1234"
        />
      </div>
      <div className="mt-3">
        <Textarea
          label="Observación"
          rows={2}
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
          disabled={isPending}
        />
      </div>

      {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Modal de gestión de tipos de activo
// ────────────────────────────────────────────────────────────────────────

function TiposModal({
  tipos,
  onClose,
}: {
  tipos: TipoActivoRead[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const [editing, setEditing] = useState<TipoActivoRead | null>(null);
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [error, setError] = useState<string | null>(null);

  function reset(): void {
    setEditing(null);
    setNombre("");
    setDescripcion("");
    setError(null);
  }

  function cargar(t: TipoActivoRead): void {
    setEditing(t);
    setNombre(t.nombre);
    setDescripcion(t.descripcion ?? "");
    setError(null);
  }

  const crearMut = useMutation({
    mutationFn: (p: TipoActivoCreate) => crearTipoActivo(p),
    onSuccess: (t) => {
      toast.success(`Tipo "${t.nombre}" creado`);
      void qc.invalidateQueries({ queryKey: ["mantenimiento", "tipos-activo"] });
      reset();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  const actualizarMut = useMutation({
    mutationFn: (p: TipoActivoUpdate) =>
      actualizarTipoActivo((editing as TipoActivoRead).id, p),
    onSuccess: (t) => {
      toast.success(`Tipo "${t.nombre}" actualizado`);
      void qc.invalidateQueries({ queryKey: ["mantenimiento", "tipos-activo"] });
      reset();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const isPending = crearMut.isPending || actualizarMut.isPending;

  function submit(): void {
    setError(null);
    if (!nombre.trim()) return setError("Nombre requerido");
    const payload = {
      nombre: nombre.trim(),
      descripcion: descripcion.trim() || null,
    };
    if (editing) actualizarMut.mutate(payload);
    else crearMut.mutate(payload);
  }

  return (
    <Modal open onClose={onClose} title="Tipos de activo" icon={Tags} size="md">
      <div className="space-y-4">
        {tipos.length === 0 ? (
          <p className="text-sm text-neutral-500">Todavía no hay tipos de activo.</p>
        ) : (
          <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
            {tipos.map((t) => (
              <li
                key={t.id}
                className="flex items-start justify-between gap-3 px-3 py-2"
              >
                <div className="min-w-0">
                  <span className="font-medium text-neutral-900">{t.nombre}</span>
                  {t.descripcion && (
                    <p className="text-xs text-neutral-500">{t.descripcion}</p>
                  )}
                </div>
                <Button
                  variant="link"
                  size="sm"
                  icon={Pencil}
                  onClick={() => cargar(t)}
                  disabled={isPending}
                >
                  Editar
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            {editing ? `Editar "${editing.nombre}"` : "Nuevo tipo de activo"}
          </p>
          <Input
            label="Nombre"
            required
            type="text"
            maxLength={80}
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            disabled={isPending}
          />
          <Textarea
            label="Descripción"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            disabled={isPending}
            rows={2}
          />

          {error && <p className="text-sm text-rose-700">{error}</p>}

          <div className="flex items-center justify-end gap-2">
            {editing && (
              <Button variant="secondary" onClick={reset} disabled={isPending}>
                Cancelar
              </Button>
            )}
            <Button onClick={submit} disabled={isPending}>
              {isPending
                ? "Guardando…"
                : editing
                  ? "Guardar cambios"
                  : "Crear tipo"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
